import {
  Box,
  Center,
  chakra,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Progress,
  Select as ChakraSelect,
  Spacer,
  Stack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { usePaginationAdmin } from '@diamond/shared/context';
import { BackofficeOrderData } from '@diamond/shared/types';
import {
  Breadcrumbs,
  Button,
  Divider,
  Icon,
  LoadingOverlay,
  Modal,
  PageLoader,
  Pagination,
  RangeDatePicker,
  showToast,
  Text,
  TextField,
} from '@diamond/shared/ui';
import {
  adminCurrencyFormatter,
  LIST_FEATURE_FLAGS,
  numberSeparator,
  statusFormatter,
  useFeatureFlag,
} from '@diamond/shared/utils';
import {
  AdminLayout,
  IndeterminateCheckbox,
} from '@diamond/sol-admin/components';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import {
  Add,
  DateRangeOutlined,
  Edit,
  ExpandMoreOutlined,
} from '@mui/icons-material';
import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import cleanDeep from 'clean-deep';
import {
  endOfDay,
  format,
  getUnixTime,
  parse,
  startOfDay,
  subDays,
} from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import shallow from 'zustand/shallow';

import { columns } from '../components/activity-columns';
import { ActivitiesSmartSearch } from '../components/activity-smart-search';
import { OrderHistoryModal } from '../components/order-history-modal';
import { StickyTable } from '../components/sticky-table';
import {
  useActivitiesData,
  useActivityExport,
  useActivityMutations,
  useCancelReasonData,
  useCustomerTypesData,
} from '../hooks';

function getDefaultParams(today: Date) {
  const dateFormat = 'dd-MM-yyyy';
  const defaultStartDate = format(subDays(today, 7), dateFormat);
  const defaultEndDate = format(today, dateFormat);

  const defaultSearchParams = {
    page: '1',
    size: '50',
    query: '',
    distribution_channel: '',
    do_automation: 'all',
    startdate: defaultStartDate,
    enddate: defaultEndDate,
  };

  return defaultSearchParams;
}

const columnHelper = createColumnHelper<BackofficeOrderData>();

type DoAutomationFilter = 'all' | 'true' | 'false';

function getDoAutomationFilter(
  value: string | undefined
): DoAutomationFilter | undefined {
  if (value === 'all' || value === 'true' || value === 'false') {
    return value;
  }

  return undefined;
}

export function ActivitiesPage() {
  const session = useAuthStore();
  const date = useDisclosure();
  const toast = useToast();
  const FF_ADMIN_PO_EFFECTIVE_DATE = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_ADMIN_PO_EFFECTIVE_DATE,
    false
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { exportActivities, isExporting } = useActivityExport();

  const [isLoadingSmartSearch, setIsLoadingSmartSearch] = useState(false);
  const [isLoadingActivitiesSearch, setIsLoadingActivitiesSearch] =
    useState(false);

  const [
    activityStatus,
    category2,
    selectedCustomerTypes,
    setActivityStatus,
    setCategory2,
  ] = useAuthStore(
    (s) => [
      s.activityStatus,
      s.category2,
      s.selectedCustomerTypes,
      s.setActivityStatus,
      s.setCategory2,
    ],
    shallow
  );
  const [selectedData, setSelectedData] = useState<Row<BackofficeOrderData>[]>(
    []
  );

  const customerTypesData = useCustomerTypesData();

  const {
    submitOrder,
    submitMultipleOrders,
    isSubmitting,
    isSubmittingMultiple,
  } = useActivityMutations();
  const processingOrderRef = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const [todayDate] = useState(() => new Date());
  const defaultSearchParams = getDefaultParams(todayDate);

  const setParams = useCallback(
    (nextParams: Record<string, string>) => {
      const prevParams = {
        ...defaultSearchParams,
        ...Object.fromEntries(searchParams.entries()),
      };

      const modifiedPrevParams = {
        ...cleanDeep(prevParams, { emptyStrings: false }),
      };

      const params = {
        ...modifiedPrevParams,
        ...cleanDeep(nextParams, { emptyStrings: false }),
      };

      setSearchParams(params);
    },
    [defaultSearchParams, searchParams, setSearchParams]
  );

  const params = useMemo(() => {
    const rawParams = {
      ...defaultSearchParams,
      ...Object.fromEntries(searchParams.entries()),
    };
    return {
      ...rawParams,
      page: Number(rawParams.page),
      size: Number(rawParams.size),
      startdate: rawParams.startdate.split('-').join('/'),
      enddate: rawParams.enddate.split('-').join('/'),
    };
  }, [defaultSearchParams, searchParams]);

  const methods = useForm({
    defaultValues: {
      status: activityStatus,
      distribution_channel: params.distribution_channel,
      do_automation: params.do_automation,
      'order_date-from': params.startdate.split('-').join('/'),
      'order_date-to': params.enddate.split('-').join('/'),
    } as FieldValues,
  });

  const searchMethods = useForm({
    defaultValues: {
      search_query: params.query,
    } as FieldValues,
  });

  const handleSubmitSearch: SubmitHandler<FieldValues> = (data) => {
    setParams({ page: '1', query: data['search_query'] });
    setIsLoadingActivitiesSearch(true);
  };

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'status' || name === 'customer_types') {
        setIsLoadingSmartSearch(true);
        setParams({ page: '1' });
      }

      if (name === 'distribution_channel') {
        setIsLoadingSmartSearch(true);
        setParams({ page: '1', distribution_channel: value[name] });
      }
      if (name === 'do_automation') {
        setIsLoadingSmartSearch(true);
        setParams({ page: '1', do_automation: value[name] });
      }
      if (name === 'order_date-from' || name === 'order_date-to') {
        setParams({
          page: '1',
          startdate: value['order_date-from']?.split('/').join('-'),
          enddate: value['order_date-to']?.split('/').join('-'),
        });
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [setParams, methods, setActivityStatus]);

  const handleFilterTanggal = (status: string) => {
    const today = new Date();
    const dateFormat = 'dd/MM/yyyy';
    let dateFrom;
    let dateEnd = format(today, dateFormat);
    switch (status) {
      case 'all':
        dateFrom = '';
        dateEnd = '';
        break;
      case '7d':
        dateFrom = format(subDays(today, 7), dateFormat);
        break;
      case '1m':
        dateFrom = format(subDays(today, 30), dateFormat);
        break;
      case '3m':
        dateFrom = format(subDays(today, 90), dateFormat);
        break;
      case '6m':
        dateFrom = format(subDays(today, 180), dateFormat);
        break;
      case '1y':
        dateFrom = format(subDays(today, 365), dateFormat);
        break;
      default:
        break;
    }

    methods.setValue('order_date-from', dateFrom);
    methods.setValue('order_date-to', dateEnd);
  };

  const links = [
    {
      title: 'Beranda',
      link: '/',
    },
    {
      title: 'Activities',
      link: '/activities',
    },
  ];

  const unixStartDate = params.startdate
    ? getUnixTime(startOfDay(parse(params.startdate, 'dd/MM/yyyy', new Date())))
    : null;
  const unixEndDate = params.enddate
    ? getUnixTime(endOfDay(parse(params.enddate, 'dd/MM/yyyy', new Date())))
    : null;

  const queryString = {
    search_query: params.query.trimStart().trimEnd(),
    page: params.page,
    size: params.size,
    search_by: '',
    order_by: '',
    direction: '',
    category_2:
      category2 === 'processed' || category2 === 'semua' ? '' : category2,
    status: activityStatus !== 'semua' ? activityStatus : '',
    start_date: unixStartDate,
    end_date: unixEndDate,
    distribution_channel: params.distribution_channel,
    do_automation: getDoAutomationFilter(params.do_automation),
    cg_1_business_segmentation: selectedCustomerTypes.map((it) => it.value),
  };

  const {
    activitiesData,
    tableData,
    pageCount,
    total,
    refetch,
    isFetching,
    isLoading,
    isFetched,
  } = useActivitiesData(queryString);

  const { cancelReasonData } = useCancelReasonData({
    page: 1,
    size: 99,
  });

  const { nextPage, previousPage, lastPage, firstPage } = usePaginationAdmin(
    params.page,
    (value) => setParams({ page: `${value}` }),
    refetch,
    pageCount
  );

  function handleSelectAll(
    values: Row<BackofficeOrderData>[],
    isChecked: boolean
  ) {
    let list = [];
    if (!isChecked) {
      for (let i = 0; i < values.length; i++) {
        const el = values[i];
        list.push(el);
      }
    } else {
      list = [];
    }
    setSelectedData(list);
  }

  const handleSelectOneRow = useCallback(
    (value: Row<BackofficeOrderData>, isCheck: boolean) => {
      const list = [...selectedData];
      if (list.length > 0 && isCheck) {
        list.splice(list.indexOf(value), 1);
        setSelectedData(list);
      } else {
        setSelectedData([...selectedData, value]);
      }
    },
    [selectedData]
  );

  const isNotSubmitted =
    selectedData.length > 0 &&
    selectedData.some((value) => value.original.status !== 'submitted');

  const checkboxColumn = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => {
          return (
            <IndeterminateCheckbox
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
              onClick={() => {
                handleSelectAll(
                  table.getRowModel().rows,
                  table.getIsAllRowsSelected()
                );
              }}
            />
          );
        },
        cell: ({ row }) => {
          return (
            <IndeterminateCheckbox
              checked={row.getIsSelected()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
              onClick={() => {
                handleSelectOneRow(row, row.getIsSelected());
              }}
            />
          );
        },
      }),
    ],
    [handleSelectOneRow]
  );

  const baseColumns = useMemo(() => {
    return columns(
      {
        customerTypeRender(id) {
          return customerTypesData.getCustomerTypeLabel(id);
        },
        cancelReasonRender(status, cancelReasonId, sapNote) {
          if (!cancelReasonId) return statusFormatter(status);
          const cancelReason =
            cancelReasonData?.cancelReasonMap[cancelReasonId].reason;
          return statusFormatter(status, cancelReason, sapNote);
        },
      },
      FF_ADMIN_PO_EFFECTIVE_DATE
    ) as ColumnDef<BackofficeOrderData>[];
  }, [cancelReasonData, customerTypesData, FF_ADMIN_PO_EFFECTIVE_DATE]);

  const tableColumns = useMemo(
    () => [...checkboxColumn, ...baseColumns],
    [baseColumns, checkboxColumn]
  );

  const isOrderMutationPending = isSubmitting || isSubmittingMultiple;

  const handleProcessOrder = async () => {
    if (processingOrderRef.current || isOrderMutationPending) {
      showToast(toast, 'info', 'Order sedang diproses, mohon tunggu');
      return;
    }

    if (selectedData.length <= 2) {
      processingOrderRef.current = true;
      try {
        if (selectedData.length === 1) {
          await submitOrder({ id: selectedData[0].original.id });
        } else {
          await submitMultipleOrders(
            selectedData.map((row) => ({ id: row.original.id }))
          );
        }
        showToast(toast, 'success', 'Berhasil Submit Order');
        setSelectedData([]);
        await refetch();
      } catch (error) {
        showToast(
          toast,
          'error',
          error instanceof Error ? error.message : 'Terjadi kesalahan'
        );
      } finally {
        processingOrderRef.current = false;
      }
    } else {
      showToast(toast, 'error', 'Hanya Dapat Proses Pesanan Hingga 2 Order');
    }
  };

  // Modal Status History per Activity Row
  const handleStatusHistory = () => {
    onOpen();
  };

  useEffect(() => {
    if (isFetched && isLoadingSmartSearch) {
      setIsLoadingSmartSearch(false);
    }
    if (isFetched && isLoadingActivitiesSearch) {
      setIsLoadingActivitiesSearch(false);
    }
  }, [isFetched, isLoadingSmartSearch, isLoadingActivitiesSearch]);

  const handleExportExcel = async () => {
    try {
      await exportActivities({
        ...queryString,
        size: 1,
      });
    } catch (error) {
      const idToast = 'error-export';
      showToast(
        toast,
        'error',
        'Pilih tanggal untuk melakukan export data.',
        idToast,
        undefined,
        5000
      );
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <PageLoader />
      </AdminLayout>
    );
  }

  const FilterButton = () => {
    return [
      <Box display={{ base: 'none', md: 'flex' }}>
        <Button
          px="4"
          variant={category2 === 'non_commodity' ? 'solid' : 'outline'}
          onClick={() => {
            if (category2 !== 'non_commodity') {
              methods.setValue('category_2', 'non_commodity');
              methods.setValue('status', 'submitted');
              setActivityStatus('submitted');
              setCategory2('non_commodity');
            }
          }}
        >
          Order Baru - Non Komoditi
        </Button>
      </Box>,
      <Box display={{ base: 'none', md: 'flex' }}>
        <Button
          px="4"
          variant={category2 === 'commodity' ? 'solid' : 'outline'}
          onClick={() => {
            if (category2 !== 'commodity') {
              methods.setValue('category_2', 'commodity');
              methods.setValue('status', 'submitted');
              setActivityStatus('submitted');
              setCategory2('commodity');
            }
          }}
        >
          Order Baru - Komoditi
        </Button>
      </Box>,
      <Box display={{ base: 'none', md: 'flex' }}>
        <Button
          px="4"
          variant={category2 === 'processed' ? 'solid' : 'outline'}
          onClick={() => {
            if (category2 !== 'processed') {
              methods.setValue('status', 'processed');
              methods.setValue('category_2', '');
              setCategory2('processed');
              setActivityStatus('processed');
            }
          }}
        >
          Order Yang Telah Diproses
        </Button>
      </Box>,
      <Box display={{ base: 'none', md: 'flex' }}>
        <Button
          px="4"
          variant={category2 === 'semua' ? 'solid' : 'outline'}
          onClick={() => {
            if (category2 !== 'semua') {
              methods.setValue('status', 'semua');
              methods.setValue('category_2', '');
              setCategory2('semua');
              setActivityStatus('semua');
            }
          }}
        >
          Semua Order
        </Button>
      </Box>,
    ];
  };

  return (
    <AdminLayout maxWidth="full">
      <LoadingOverlay
        isLoading={isLoadingSmartSearch}
        text="Memuat Data berdasarkan Smart Search"
      />
      <LoadingOverlay
        isLoading={isLoadingActivitiesSearch}
        text="Memuat Data berdasarkan pencarian"
      />
      <LoadingOverlay
        isLoading={isSubmitting}
        text="Submitting Your Order, Please Don't Refresh This Page!"
      />
      <Box px={{ base: 4, md: 12 }} pb={8}>
        <Box paddingBottom={4}>
          <Breadcrumbs links={links} useRouterDom />
        </Box>
        <FormProvider {...methods}>
          <Stack spacing={4}>
            <Flex
              alignItems={{ base: 'flex-start', lg: 'center' }}
              gap={4}
              justifyContent="space-between"
              wrap="wrap"
            >
              <HStack spacing={3}>
                {selectedData.length > 0 && (
                  <Menu autoSelect={false}>
                    <MenuButton
                      as={Button}
                      aria-label="menu"
                      variant="solid"
                      px={0}
                      isDisabled={isOrderMutationPending}
                    >
                      <Icon as={Edit} />
                    </MenuButton>
                    <Portal>
                      {selectedData.length === 1 && (
                        <MenuList>
                          <MenuItem
                            onClick={handleProcessOrder}
                            isDisabled={
                              isNotSubmitted || isOrderMutationPending
                            }
                          >
                            Proses Pesanan
                          </MenuItem>
                          <MenuItem onClick={handleStatusHistory}>
                            Riwayat Pesanan
                          </MenuItem>
                        </MenuList>
                      )}
                      {selectedData.length > 1 && (
                        <MenuList>
                          <MenuItem
                            onClick={handleProcessOrder}
                            isDisabled={
                              isNotSubmitted || isOrderMutationPending
                            }
                          >
                            Process Activities
                          </MenuItem>
                        </MenuList>
                      )}
                    </Portal>
                  </Menu>
                )}
                <Text color="#2F343A" fontSize="24px" fontWeight="semibold">
                  {numberSeparator(total)} Hasil
                </Text>
              </HStack>
              <HStack alignItems="center" spacing={3} wrap="wrap">
                <Box display={{ base: 'none', md: 'flex' }} zIndex="100">
                  <Menu>
                    <MenuButton
                      as={Button}
                      h="46px"
                      leftIcon={<DateRangeOutlined />}
                      minW="236px"
                      rightIcon={<ExpandMoreOutlined />}
                      fontSize="14px"
                      fontWeight="normal"
                      borderColor="#9CA3AF"
                      alignContent="center"
                    >
                      {methods.watch('order_date-from') &&
                      methods.watch('order_date-to')
                        ? `${methods.watch(
                            'order_date-from'
                          )} s/d ${methods.watch('order_date-to')}`
                        : 'Pilih Tanggal Order'}
                    </MenuButton>

                    <MenuList>
                      <MenuItem onClick={() => handleFilterTanggal('7d')}>
                        7 Hari Terakhir
                      </MenuItem>
                      <MenuItem onClick={() => handleFilterTanggal('1m')}>
                        1 Bulan Terakhir
                      </MenuItem>
                      <MenuItem onClick={() => handleFilterTanggal('3m')}>
                        3 Bulan Terakhir
                      </MenuItem>
                      <MenuItem onClick={() => handleFilterTanggal('6m')}>
                        6 Bulan Terakhir
                      </MenuItem>
                      <MenuItem onClick={() => handleFilterTanggal('1y')}>
                        1 Tahun Terakhir
                      </MenuItem>
                      <MenuItem onClick={date.onOpen}>Pilih Tanggal</MenuItem>
                    </MenuList>
                  </Menu>
                </Box>
                <chakra.form
                  w="232px"
                  onSubmit={searchMethods.handleSubmit(handleSubmitSearch)}
                >
                  <TextField
                    h="46px"
                    name="search_query"
                    placeholder="Cari ..."
                    register={searchMethods.register}
                    borderColor="#9CA3AF"
                    fontSize="14px"
                    registerOptions={{
                      onBlur: (e) => {
                        e.preventDefault();
                        const search_query =
                          searchMethods.watch('search_query');
                        handleSubmitSearch({ search_query });
                      },
                    }}
                  />
                </chakra.form>
                <Box
                  alignItems="center"
                  border="1px solid"
                  borderColor="#0000A3"
                  borderRadius="8px"
                  color="#0000A3"
                  display="flex"
                  fontSize="12px"
                  fontWeight="semibold"
                  h="46px"
                  px={4}
                  whiteSpace="nowrap"
                >
                  Total Nilai Order:{' '}
                  {adminCurrencyFormatter(activitiesData?.total)}
                </Box>
              </HStack>
            </Flex>
            <Divider />
            {session.userRole === 'cs_katalog' ||
            session.userRole === 'cs_cabang' ? (
              <Stack
                p="5"
                bgColor="#F3F4F5"
                borderWidth="1px"
                borderColor="#D9D9D9"
                borderRadius="lg"
                direction="row"
                spacing="5"
                w="full"
                pb="6"
                overflow="auto"
              >
                {FilterButton().map((button) => button)}
              </Stack>
            ) : null}
            <Flex
              alignItems="flex-end"
              gap={4}
              justifyContent="space-between"
              wrap="wrap"
            >
              <ActivitiesSmartSearch />
              <Button
                h="46px"
                leftIcon={<Add />}
                variant="solid"
                onClick={handleExportExcel}
                isLoading={isExporting}
                isDisabled={isExporting}
                minW={153}
              >
                Export Excel
              </Button>
            </Flex>
            <Box width="full">
              <FetchingProgressIndicator isFetching={isFetching}>
                <StickyTable
                  columns={tableColumns}
                  data={tableData}
                  slices={5}
                />
              </FetchingProgressIndicator>
              <Center my={8}>
                <HStack spacing={6}>
                  <Pagination
                    currentPage={params.page}
                    totalPage={pageCount}
                    nextPage={nextPage}
                    firstPage={firstPage}
                    lastPage={lastPage}
                    previousPage={previousPage}
                  />
                  <HStack spacing={3}>
                    <Text color="#8A8F98" fontSize="14px">
                      per halaman
                    </Text>
                    <ChakraSelect
                      value={params.size}
                      onChange={(event) =>
                        setParams({ page: '1', size: event.target.value })
                      }
                      w="56px"
                      h="40px"
                    >
                      <option value={15}>15</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </ChakraSelect>
                  </HStack>
                </HStack>
              </Center>
            </Box>
          </Stack>
          <Modal
            size="2xl"
            title="Pilih Tanggal"
            name="date_order"
            isOpen={date.isOpen}
            onClose={date.onClose}
            onSubmit={() => {
              date.onClose();
            }}
          >
            <Center>
              <Stack>
                <DateFilter />
              </Stack>
            </Center>
          </Modal>
          <OrderHistoryModal
            isOpen={isOpen}
            onClose={onClose}
            selectedDataId={selectedData[0]?.original.id || ''}
          />
        </FormProvider>
      </Box>
    </AdminLayout>
  );
}

function FetchingProgressIndicator({
  children,
  isFetching,
}: React.PropsWithChildren<{ isFetching: boolean }>) {
  return (
    <>
      {isFetching ? (
        <Progress size="xs" isIndeterminate colorScheme="messenger" />
      ) : (
        <Box height="2px"></Box>
      )}
      {children}
      {isFetching ? (
        <Progress size="xs" isIndeterminate colorScheme="messenger" />
      ) : (
        <Box height="2px"></Box>
      )}
    </>
  );
}

function DateFilter() {
  const selectedDateRangeState = useState<DateRange | undefined>();
  const { setValue } = useFormContext();
  return (
    <>
      <Flex>
        <Spacer />
        <Button
          color="red"
          variant="link"
          border="none"
          fontWeight="normal"
          onClick={() => {
            setValue('order_date-from', '');
            setValue('order_date-to', '');
          }}
        >
          Hapus
        </Button>
      </Flex>
      <RangeDatePicker
        name="order_date"
        mode="range"
        selectedDateRangeState={selectedDateRangeState}
      />
    </>
  );
}
